import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Microscope, Heart, Dna, Apple, Sparkles, Check, Zap, Search, Clock, PawPrint, Camera, Shield, Brain, Bone, Droplets, Scan, Wand2 } from 'lucide-react';
import { useAnalysis } from '../AnalysisContext';
import { motion, AnimatePresence } from 'framer-motion';
import { trackPixelEvent, PIXEL_EVENTS } from '../utils/pixelService.js';
import { trackGaEvent } from '../utils/gaService.js';
import { trackHotjarEvent } from '../utils/hotjarService.js';

// Enhanced analysis steps with more engaging icons and descriptions
const analysisSteps = [
  {
    icon: Camera,
    text: "Procesando imágenes",
    subtext: "Optimizando calidad y resolución",
    progressRange: [0, 20],
    color: "#FB923C" // amber-400
  },
  {
    icon: Scan,
    text: "Escaneando patrones",
    subtext: "Identificando características únicas",
    progressRange: [20, 40],
    color: "#F97316" // orange-500
  },
  {
    icon: Brain,
    text: "Analizando salud",
    subtext: "Evaluando signos vitales",
    progressRange: [40, 60],
    color: "#EA580C" // orange-600
  },
  {
    icon: Bone,
    text: "Identificando raza",
    subtext: "Comparando con base de datos",
    progressRange: [60, 80],
    color: "#C2410C" // orange-700
  },
  {
    icon: Wand2,
    text: "Creando reporte",
    subtext: "Generando recomendaciones",
    progressRange: [80, 100],
    color: "#9A3412" // orange-800
  }
];

const PetAnalysisLoader = ({ progress, files }) => {
  const { petName } = useAnalysis();
  const [startTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [scanLine, setScanLine] = useState(0);
  const [scanDirection, setScanDirection] = useState(1);
  const [xrayIntensity, setXrayIntensity] = useState(0.35);
  const [dataBits, setDataBits] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [currentMode, setCurrentMode] = useState("normal");
  const [pawPrints, setPawPrints] = useState([]);
  const [showTip, setShowTip] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const canvasRef = useRef(null);
  const petImageRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const isRendered = useRef(true);

  // Pet-friendly tips that appear occasionally
  const petTips = [
    "Analizando el pelaje para detectar signos de salud...",
    "Midiendo proporciones para identificar la raza...",
    "Evaluando patrones faciales únicos...",
    "Comparando con nuestra base de datos canina...",
    "Preparando recomendaciones personalizadas..."
  ];

  // Handle canvas resizing
  const resizeCanvas = useCallback(() => {
    if (!canvasRef.current || !petImageRef.current) return;
    
    const container = petImageRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;
  }, []);

  // Create object URL for the first file if available with optimized loading
  const mainImage = useMemo(() => {
    if (files && files.length > 0 && files[0] instanceof File) {
      const url = URL.createObjectURL(files[0]);
      // Preload the image
      const img = new Image();
      img.src = url;
      imageRef.current = img;
      
      img.onload = () => {
        setImageLoaded(true);
        requestAnimationFrame(() => {
          resizeCanvas();
        });
      };
      return url;
    }
    return null;
  }, [files, resizeCanvas]);

  // Update elapsed time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [startTime]);

  // Clean up object URL when component unmounts or files change
  useEffect(() => {
    return () => {
      if (mainImage) {
        URL.revokeObjectURL(mainImage);
      }
    };
  }, [mainImage]);

  // Handle window resize
  useEffect(() => {
    if (imageLoaded) {
      window.addEventListener('resize', resizeCanvas);
      resizeCanvas();
      return () => window.removeEventListener('resize', resizeCanvas);
    }
  }, [imageLoaded, resizeCanvas]);

  // Toggle between normal and x-ray modes
  useEffect(() => {
    if (!imageLoaded) return;

    if (progress > 30 && progress < 75) {
      const switchInterval = setInterval(() => {
        setCurrentMode(prev => {
          if (prev === "normal") {
            setXrayIntensity(0.65);
            setTimeout(() => setXrayIntensity(0.35), 2000);
            return "xray";
          } else {
            return "normal";
          }
        });
      }, 6000);

      return () => clearInterval(switchInterval);
    } else {
      setCurrentMode("normal");
    }
  }, [progress, imageLoaded]);

  // Animate scan line
  useEffect(() => {
    if (!mainImage || !imageLoaded) return;

    const interval = setInterval(() => {
      setScanLine(prev => {
        const newPosition = prev + (scanDirection * 2.5);

        // Change direction at boundaries
        if (newPosition >= 98) {
          setScanDirection(-1);
          return 98;
        } else if (newPosition <= 2) {
          setScanDirection(1);
          return 2;
        }

        return newPosition;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [mainImage, scanDirection, imageLoaded]);

  // Show tips occasionally
  useEffect(() => {
    if (!imageLoaded) return;

    const tipInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        setTipIndex(prev => (prev + 1) % petTips.length);
        setShowTip(true);
        setTimeout(() => setShowTip(false), 4000);
      }
    }, 6000);
    
    return () => clearInterval(tipInterval);
  }, [imageLoaded]);

  // Add paw prints and handle data bits
  useEffect(() => {
    if (!mainImage || !imageLoaded || !petImageRef.current) return;

    // Add paw prints when scan line changes direction
    if ((scanLine >= 98 || scanLine <= 2) && Math.random() > 0.6) {
      const newPawPrint = {
        id: Date.now(),
        x: 10 + Math.random() * 80,
        y: 10 + Math.random() * 80,
        size: 8 + Math.random() * 6,
        rotation: Math.random() * 360,
        opacity: 0.7,
        fadeOut: false
      };

      setPawPrints(prev => [...prev.slice(-5), newPawPrint]);

      // Start fade out after a delay
      setTimeout(() => {
        setPawPrints(prev => 
          prev.map(p => p.id === newPawPrint.id ? {...p, fadeOut: true} : p)
        );

        // Remove after fade out
        setTimeout(() => {
          setPawPrints(prev => prev.filter(p => p.id !== newPawPrint.id));
        }, 1500);
      }, 2000);
    }

    // Add data bits with scan line movement
    if (scanLine % 8 === 0) {
      const side = Math.random() > 0.5 ? 'left' : 'right';
      const newBit = {
        id: Date.now(),
        x: side === 'left' ? 5 : 95,
        y: scanLine,
        size: 1 + Math.random() * 2,
        decay: 0.94 + Math.random() * 0.03,
        opacity: 0.8,
        life: 30
      };

      setDataBits(prev => [...prev, newBit]);
    }

    // Update existing data bits
    setDataBits(prev => {
      return prev
        .map(bit => ({
          ...bit,
          opacity: bit.opacity * bit.decay,
          life: bit.life - 1
        }))
        .filter(bit => bit.life > 0);
    });

  }, [mainImage, scanLine, imageLoaded]);

  // Add markers occasionally
  useEffect(() => {
    if (!imageLoaded || !mainImage) return;

    if ((scanLine >= 98 || scanLine <= 2) && Math.random() > 0.7) {
      const newMarker = {
        id: `marker-${Date.now()}`,
        x: 20 + Math.random() * 60,
        y: 20 + Math.random() * 60,
        size: 2 + Math.random() * 3,
        type: Math.random() > 0.5 ? 'health' : 'breed',
        pulse: true
      };

      setMarkers(prev => [
        ...prev.slice(-6),
        newMarker
      ]);

      // Stop pulse after a while
      setTimeout(() => {
        setMarkers(prev => prev.map(m =>
          m.id === newMarker.id ? { ...m, pulse: false } : m
        ));
      }, 3000);
    }
  }, [scanLine, mainImage, imageLoaded]);

  // Draw on canvas
  useEffect(() => {
    if (!canvasRef.current || !mainImage) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw markers
    markers.forEach(marker => {
      const { x, y, size, type, pulse } = marker;
      const color = type === 'health' ? '#F97316' : '#3B82F6';

      const markerX = canvas.width * (x / 100);
      const markerY = canvas.height * (y / 100);

      // Draw center point
      ctx.beginPath();
      ctx.arc(markerX, markerY, size, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      // Draw pulse effect
      if (pulse) {
        ctx.beginPath();
        ctx.arc(markerX, markerY, size * 2, 0, Math.PI * 2);
        ctx.strokeStyle = `${color}40`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    });

    // Draw data bits
    dataBits.forEach(bit => {
      ctx.fillStyle = `rgba(249, 115, 22, ${bit.opacity})`;
      ctx.fillRect(
        canvas.width * (bit.x / 100),
        canvas.height * (bit.y / 100),
        bit.size,
        bit.size
      );
    });

    // Draw paw prints
    pawPrints.forEach(paw => {
      const pawX = canvas.width * (paw.x / 100);
      const pawY = canvas.height * (paw.y / 100);
      
      ctx.save();
      ctx.translate(pawX, pawY);
      ctx.rotate(paw.rotation * Math.PI / 180);
      
      // Set opacity based on fade state
      ctx.globalAlpha = paw.fadeOut ? paw.opacity * 0.5 : paw.opacity;
      
      // Draw paw print
      const size = paw.size;
      
      // Main pad
      ctx.beginPath();
      ctx.ellipse(0, 0, size * 0.8, size * 1, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(249, 115, 22, 0.3)';
      ctx.fill();
      
      // Toe pads
      for (let i = 0; i < 4; i++) {
        const angle = (i * Math.PI / 2) + (Math.PI / 4);
        const padX = Math.cos(angle) * size * 0.9;
        const padY = Math.sin(angle) * size * 0.9;
        
        ctx.beginPath();
        ctx.ellipse(padX, padY, size * 0.4, size * 0.5, angle, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(249, 115, 22, 0.3)';
        ctx.fill();
      }
      
      ctx.restore();
    });

    // Draw grid for X-ray mode
    if (currentMode === "xray") {
      const gridSize = canvas.width > 400 ? 20 : 15;
      ctx.strokeStyle = "rgba(255, 152, 0, 0.15)";
      ctx.lineWidth = 0.5;

      // Horizontal lines
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Vertical lines
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
    }
  }, [markers, dataBits, mainImage, pawPrints, currentMode]);

  // Get current step based on progress
  const getCurrentStep = () => {
    return analysisSteps.find(step => {
    const [min, max] = step.progressRange;
      return progress >= min && progress < max;
    }) || analysisSteps[analysisSteps.length - 1];
  };

  const currentStep = getCurrentStep();
  const isXrayMode = currentMode === "xray";

  // Format remaining time
  const formatRemainingTime = () => {
    const totalEstimatedSeconds = 60;
    const remainingSeconds = Math.max(0, totalEstimatedSeconds - elapsedTime);
    
    if (remainingSeconds <= 0) return "Finalizando...";
    
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  // Track loading progress for Facebook Pixel
  useEffect(() => {
    if (!isRendered.current) return;
    
    const trackProgressMilestone = (milestone) => {
      // Parámetros comunes para el evento de progreso
      const progressParams = {
        content_name: 'Pet Analysis',
        progress_milestone: milestone
      };
      
      // Rastrear en Facebook Pixel
      trackPixelEvent(PIXEL_EVENTS.SCAN_PROGRESS, progressParams);
      
      // Rastrear en Google Analytics
      trackGaEvent('scan_progress', progressParams);
      
      // Rastrear en Hotjar
      trackHotjarEvent(`scan_progress_${milestone.replace('%', '')}`);
    };
    
    if (progress >= 25 && progress < 26) {
      trackProgressMilestone('25%');
    } else if (progress >= 50 && progress < 51) {
      trackProgressMilestone('50%');
    } else if (progress >= 75 && progress < 76) {
      trackProgressMilestone('75%');
    } else if (progress >= 99) {
      trackProgressMilestone('100%');
    }
  }, [progress]);

  // Track when component mounts/unmounts
  useEffect(() => {
    isRendered.current = true;
    
    // Parámetros comunes para el evento de visualización del loader
    const loaderParams = {
      content_name: 'Pet Analysis Loader',
      files_count: files?.length || 0
    };
    
    // Initial tracking when loader appears - Facebook Pixel
    trackPixelEvent(PIXEL_EVENTS.LOADER_DISPLAYED, loaderParams);
    
    // Initial tracking when loader appears - Google Analytics
    trackGaEvent('loader_displayed', loaderParams);
    
    // Initial tracking when loader appears - Hotjar
    trackHotjarEvent('analysis_started');
    
    return () => {
      isRendered.current = false;
    };
  }, [files]);

  return (
    <div className="fixed inset-0 z-50" ref={containerRef}>
      <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-md" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full max-w-2xl"
          >
            <Card className="relative bg-white/95 backdrop-blur-sm shadow-2xl rounded-2xl overflow-hidden border-none">
              {/* Enhanced Progress Bar */}
              <div className="absolute top-0 left-0 right-0 h-2.5 bg-gradient-to-r from-gray-100/50 to-gray-100/30 overflow-hidden rounded-t-2xl z-10">
                <motion.div
                  className="h-full relative overflow-hidden"
                style={{
                  width: `${progress}%`,
                    background: `linear-gradient(90deg, ${currentStep.color}40 0%, ${currentStep.color} 100%)`
                  }}
                  initial={{ width: "0%" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  {/* Shimmer effect */}
                  <motion.div
                    className="absolute inset-0 w-full h-full"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                      transform: 'skewX(-20deg)',
                    }}
                    animate={{ x: ['100%', '-100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                </motion.div>
              </div>
              
              {/* Main Content */}
              <div className="p-6 sm:p-8">
                {/* Enhanced Header */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      className="relative"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <div className="bg-gradient-to-br from-amber-100 to-orange-100 p-3 rounded-xl shadow-md">
                        <PawPrint className="h-6 w-6 text-orange-500" />
                      </div>
                      <motion.div 
                        className="absolute -top-1 -right-1 h-3.5 w-3.5 bg-green-400 rounded-full ring-2 ring-white"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {petName ? `Analizando a ${petName}` : "Analizando a tu mascota"}
                      </h3>
                      <div className="flex items-center text-sm text-orange-600 font-medium">
                        <Clock className="w-4 h-4 mr-1.5" />
                        <span>{formatRemainingTime()}</span>
                      </div>
              </div>
            </div>

                  <div className="flex items-center gap-2">
                  {isXrayMode && (
                      <motion.div 
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        className="bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 px-3 py-1.5 rounded-full font-medium flex items-center shadow-sm"
                      >
                        <Zap className="w-4 h-4 mr-1.5" />
                      RAYOS X
                      </motion.div>
                    )}
                    <motion.div 
                      className="bg-gradient-to-br from-orange-100 to-amber-50 text-orange-600 text-base font-semibold rounded-xl h-10 px-3 flex items-center justify-center shadow-sm"
                      animate={{ 
                        boxShadow: ['0 1px 2px rgba(0,0,0,0.05)', '0 4px 6px rgba(0,0,0,0.1)', '0 1px 2px rgba(0,0,0,0.05)']
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {Math.round(progress)}%
                    </motion.div>
                    </div>
              </div>

                {/* Enhanced Image Analysis Area with Loading Optimization */}
                {mainImage && (
                  <div className="relative">
                    <div ref={petImageRef} className="relative w-full h-72 sm:h-80 rounded-2xl overflow-hidden shadow-xl border border-orange-100">
                      {/* Loading Placeholder */}
                      <AnimatePresence mode="sync">
                        {!imageLoaded && (
                          <motion.div
                            initial={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center"
                          >
                            <motion.div
                              animate={{ 
                                scale: [1, 1.2, 1],
                                rotate: [0, 360]
                              }}
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                              className="w-12 h-12 rounded-full border-4 border-orange-200 border-t-orange-500"
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Base Image with Gradient Overlay and Loading Optimization */}
                      <motion.div 
                        className="absolute inset-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: imageLoaded ? 1 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <img
                          src={mainImage}
                          alt="Mascota"
                          className="w-full h-full object-cover object-center"
                          loading="eager"
                          decoding="sync"
                          onLoad={() => setImageLoaded(true)}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      </motion.div>

                      {/* Enhanced X-ray Effect with Loading Check */}
                      <AnimatePresence mode="sync">
                        {isXrayMode && imageLoaded && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: xrayIntensity }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-0"
                            style={{
                              backgroundImage: `url(${mainImage})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              filter: 'invert(1) hue-rotate(200deg) brightness(1.8) contrast(1.4) saturate(0.8)',
                              mixBlendMode: 'color-dodge'
                            }}
                          >
                            {/* X-ray Grid Overlay */}
                            <div
                              className="absolute inset-0"
                              style={{
                                backgroundImage: 'linear-gradient(0deg, rgba(96, 165, 250, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(96, 165, 250, 0.1) 1px, transparent 1px)',
                                backgroundSize: '20px 20px',
                                opacity: 0.5
                              }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Only show scan effects when image is loaded */}
                      {imageLoaded && (
                        <>
                          {/* Enhanced Scan Line */}
                          <motion.div
                            className="absolute left-0 right-0 h-2 pointer-events-none"
                            style={{
                              top: `${scanLine}%`,
                              background: `linear-gradient(90deg, 
                                rgba(255,255,255,0) 0%, 
                                ${isXrayMode ? '#60A5FA' : currentStep.color} 50%, 
                                rgba(255,255,255,0) 100%)`,
                              boxShadow: `0 0 20px 4px ${isXrayMode ? 'rgba(59, 130, 246, 0.4)' : `${currentStep.color}60`}`
                            }}
                          />

                          {/* Canvas for interactive elements */}
                          <canvas
                            ref={canvasRef}
                            className="absolute inset-0 pointer-events-none"
                          />
                        </>
                      )}

                      {/* Enhanced Scan Interface Overlay */}
                      <div className="absolute inset-0">
                        {/* Corner brackets with animations */}
                        <motion.div 
                          className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-orange-300/80 rounded-tl"
                          animate={{ opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                        <motion.div 
                          className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-orange-300/80 rounded-tr"
                          animate={{ opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                        />
                        <motion.div 
                          className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-orange-300/80 rounded-bl"
                          animate={{ opacity: [0.8, 1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                        />
                        <motion.div 
                          className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-orange-300/80 rounded-br"
                          animate={{ opacity: [1, 0.6, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
                        />

                        {/* Enhanced Status Indicators */}
                        <div className="absolute top-3 left-3 text-sm font-mono font-medium bg-white/80 backdrop-blur px-3 py-1.5 rounded-lg shadow-sm border border-orange-200/50">
                          <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                            DogScan AI
                          </span>
  </div>
                        
                        <motion.div 
                          className="absolute top-3 right-3 text-sm font-mono font-medium bg-white/80 backdrop-blur px-3 py-1.5 rounded-lg shadow-sm border border-orange-200/50 flex items-center gap-2"
                          animate={{ 
                            boxShadow: ['0 1px 2px rgba(0,0,0,0.05)', '0 4px 6px rgba(0,0,0,0.1)', '0 1px 2px rgba(0,0,0,0.05)']
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            <Search className="w-4 h-4 text-orange-500" />
                          </motion.div>
                          <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                            {isXrayMode ? "ESCANEANDO" : "ANALIZANDO"}
      </span>
                        </motion.div>
                        
                        {/* Enhanced Floating Tip */}
                        <AnimatePresence mode="sync">
                          {showTip && (
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur text-sm text-gray-700 px-4 py-2 rounded-xl shadow-lg border border-orange-100 max-w-[90%] text-center"
                            >
                              {petTips[tipIndex]}
                            </motion.div>
                          )}
                        </AnimatePresence>
</div>
</div>

                    {/* Enhanced Current Step Indicator */}
                    <motion.div
                      className="mt-6 bg-gradient-to-r from-orange-50/80 to-amber-50/80 backdrop-blur rounded-xl p-4 shadow-lg border border-orange-100"
                      animate={{ 
                        boxShadow: ['0 2px 4px rgba(0,0,0,0.05)', '0 6px 12px rgba(0,0,0,0.1)', '0 2px 4px rgba(0,0,0,0.05)']
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <div className="flex items-center gap-4">
                        <motion.div 
                          className="bg-white p-3 rounded-xl shadow-sm"
                          whileHover={{ scale: 1.05 }}
                        >
                          <currentStep.icon className="w-6 h-6" style={{ color: currentStep.color }} />
                        </motion.div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="text-base font-semibold text-gray-900">{currentStep.text}</h3>
                            <span className="text-sm text-orange-600 font-medium">{currentStep.subtext}</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div 
                              className="h-full rounded-full relative overflow-hidden"
                              style={{ 
                                background: `linear-gradient(90deg, ${currentStep.color}80, ${currentStep.color})`,
                                width: `${((progress - currentStep.progressRange[0]) / (currentStep.progressRange[1] - currentStep.progressRange[0])) * 100}%`
                              }}
                              initial={{ width: "0%" }}
                              animate={{ width: `${((progress - currentStep.progressRange[0]) / (currentStep.progressRange[1] - currentStep.progressRange[0])) * 100}%` }}
                              transition={{ duration: 0.5 }}
                            >
                              {/* Shimmer effect */}
                              <motion.div
                                className="absolute inset-0 w-full h-full"
                                style={{
                                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                  transform: 'skewX(-20deg)',
                                }}
                                animate={{ x: ['100%', '-100%'] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                              />
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                        </div>
                      )}

                {/* Enhanced Footer */}
                <motion.div 
                  className="mt-6 flex items-center justify-center"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="text-center max-w-md">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <motion.div
                        animate={{ 
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0]
                        }}
                        transition={{ duration: 4, repeat: Infinity }}
                      >
                        <Shield className="w-5 h-5 text-orange-500" />
                      </motion.div>
                      <span className="text-base font-semibold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                        Análisis seguro y privado
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Nuestro sistema de IA está procesando las imágenes con la más alta precisión para brindarte el mejor análisis para tu mascota
                    </p>
              </div>
                </motion.div>
            </div>
          </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PetAnalysisLoader;